import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Illus from 'pages/Authentication/components/AuthTemplate/assets/login.png'
import AuthTemplate from 'pages/Authentication/components/AuthTemplate'
import AuthTitle from 'pages/Authentication/components/AuthTitle'
import GraphQLHandler from 'components/GraphQLHandler'
import i18n, { TFunction } from 'i18next'
import * as Yup from 'yup'
import { Formik } from 'formik'
import ValidateOnEnter from 'components/ValidateOnEnter'
import Form from 'components/Form'
import Input from 'components/Input'
import SubmitButton from 'components/SubmitButton'
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useMutation } from '@apollo/client'
import {
  FORGOT_CREDENTIALS,
  ForgotCredentialsResult,
  ForgotCredentialsVariables
} from 'pages/Authentication/ForgotCredentials/mutations'
import { displayErrors, DoMutation, hasNoError } from 'globals/graphql'
import { BackLink } from '../components/styles'
import { SetState } from 'globals'
import StateDialog from 'components/StateDialog'

interface ForgotCredentialsForm {
  name?: string,
  email?: string,
  message?: string
}

const initialValues: ForgotCredentialsForm = {}

const validation = (t: TFunction): Yup.SchemaOf<ForgotCredentialsForm> => Yup.object({
  name: Yup.string()
      .required(t('global.error.required-f', { field: t('global.name') })),
  email: Yup.string()
      .required(t('global.error.required', { field: t('global.email') })),
  message: Yup.string()
      .required(t('global.error.required', { field: t('global.message') }))
})

const ForgotCredentials = () => {
  const { t } = useTranslation()
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

  const [isReCaptchaVerified, setReCaptchaVerified] = useState(false)

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
      language={i18n.language}
    >
      <AuthTemplate
        rightPartImg={Illus}
        headerRightContent={
          <BackLink to="/login">
            {t('forgot-credentials.back')}
          </BackLink>
        }
      >
        <AuthTitle
          title={
            <Trans i18nKey="forgot-credentials.title">
              Demande de récupération<br/><b>du mot de passe/identifiant</b>
            </Trans>
          }
        />

        <GraphQLHandler
          mutations={{
            forgotCredentials: useMutation<
            ForgotCredentialsResult, ForgotCredentialsVariables
            >(FORGOT_CREDENTIALS, displayErrors)
          }}
        >
          {({ mutations: { forgotCredentials: [doForgotCredentials, forgotCredentialsData] } }) =>
            <Formik
              initialValues={initialValues}
              validationSchema={validation(t)}
              onSubmit={onSubmitForgotPasswordForm(doForgotCredentials, setShowConfirmationDialog)}
              isInitialValid={validation(t).isValidSync(initialValues)}
            >
              {({ handleSubmit, isValid }) =>
                <ValidateOnEnter
                  handleSubmit={handleSubmit}
                  name="forgotCredentials"
                >
                  <Form>
                    <Input
                      name="name"
                      label={t('global.name')}
                      defaultFocus
                    />

                    <Input
                      name="email"
                      label={t('global.email')}
                    />

                    <Input
                      name="message"
                      type="multiline"
                      label={t('global.message')}
                    />

                    <GoogleReCaptcha onVerify={onReCaptchaVerified(setReCaptchaVerified)} />

                    <SubmitButton
                      text={t('global.send')}
                      disabled={!(isValid && isReCaptchaVerified)}
                      loading={forgotCredentialsData.loading}
                    />
                  </Form>
                </ValidateOnEnter>
              }
            </Formik>
          }
        </GraphQLHandler>

        <StateDialog
          state="success"
          visible={showConfirmationDialog}
          onClose={() => setShowConfirmationDialog(false)}
        >
          <Trans i18nKey="forgot-credentials.confirm">
            Votre demande a bien été prise en compte.<b/>Vous recevrez prochainement un mail avec vos nouveaux accès.
          </Trans>
        </StateDialog>
      </AuthTemplate>
    </GoogleReCaptchaProvider>
  )
}

const onReCaptchaVerified = (setReCaptchaVerified: SetState<boolean>) => () =>
  setReCaptchaVerified(true)

const onSubmitForgotPasswordForm = (
    doForgotCredentials: DoMutation<ForgotCredentialsResult, ForgotCredentialsVariables>,
    setShowConfirmationDialog: SetState<boolean>
) => (values: ForgotCredentialsForm) => {
  if (values.name && values.email && values.message) {
    doForgotCredentials({
      variables: {
        name: values.name,
        email: values.email,
        message: values.message
      }
    }).then(
        hasNoError(() =>
          setShowConfirmationDialog(true)
        )
    )
  }
}

export default ForgotCredentials
